import React, {type RefObject} from "react";
import {useMessages} from "i18n";
import {
	overLimitAction, type OverLimitAction, type OverLimitPricing, type OverLimitResolution,
} from "../overLimit/overLimitOffer";

/** Makes an unhandled {@link OverLimitAction} a COMPILE error rather than a silent empty surface. */
function assertNever(action: never): never {
	throw new Error(`unhandled over-limit action: ${String(action)}`);
}
import {OverLimitRoot, PayAction} from "./OverLimitNotice.styles";

interface Props {
	/** The rule module's answer. This component renders it and decides nothing. */
	resolution: OverLimitResolution;
	/**
	 * Which rule prices the send. A plan wall states the Instant sentence or the hosted one; the press under either
	 * opens the same sized plan dialog (kitecorp/transferify-ui#3800).
	 */
	pricing: OverLimitPricing;
	/** A placed plan order is settling — the strip says so and offers no control. */
	confirming: boolean;
	/** Re-asks a quote that gave up. */
	onRetry: () => void;
	/**
	 * Opens the sized plan dialog, the one action either plan wall offers (kitecorp/transferify-api-service#3005, Q5
	 * for Instant; kitecorp/transferify-ui#3800 for hosted), where the api offered plans.
	 */
	onChoosePlan: () => void;
	/**
	 * The strip itself, so the payment dialog can hand focus BACK to it.
	 *
	 * The dialog's opener is this surface's plan press, and on the path that matters — a PLACED order —
	 * that button is gone by the time focus is restored: the same commit that closes the sheet moves the
	 * strip to its "confirming" rung. Restoring onto a detached node drops focus to `<body>`,
	 * so the region that REPLACED the dialog takes it instead.
	 */
	rootRef?: RefObject<HTMLDivElement | null>;
}

/**
 * The over-cap wall (ui#1454): what a FREE sender sees when their selection exceeds the free cap. Until
 * ui#1454 the same state was a DEAD END — an upgrade nudge and a disabled Send — and converting it, rather
 * than deleting the enforcement, is the whole of this component. Its way forward is a plan since
 * kitecorp/transferify-ui#3800; no one-off is sold.
 *
 * <b>EXACTLY ONE CTA, and no subscription pitch (owner constraint, verbatim).</b> The upsell is a
 * separate, later moment; putting it beside this purchase turns one offer into two competing ones. That
 * is not left to reviewer vigilance: `scripts/over-limit-single-cta-sweep.mjs` parses this file and its
 * styles and fails the build on a second interactive control or any upgrade/pricing reference.
 *
 * It renders state and computes nothing. Every branch below is a `kind` from
 * {@link ../overLimit/overLimitOffer.resolveOverLimit}.
 */
export function OverLimitNotice(
	{resolution, pricing, confirming, onRetry, onChoosePlan, rootRef}: Props,
) {
	const copy = useMessages().sender.overLimit;
	const planRequiredSentence = pricing === "hosted" ? copy.hostedPlanRequired : copy.instantPlanRequired;
	/**
	 * Every rung renders the same strip, and the attributes are shared rather than repeated per branch:
	 * the ref is the payment dialog's focus destination, and the live region is what announces a rung
	 * change the sender caused but can no longer see (the sheet that took their card has closed).
	 */
	const strip = (children: React.ReactNode) => (
		<OverLimitRoot ref={rootRef} tabIndex={-1} role="status" aria-live="polite">{children}</OverLimitRoot>
	);

	if (resolution.kind === "clear" || resolution.kind === "upgradeOnly" || resolution.kind === "signInRequired") {
		// `upgradeOnly` is a PAID plan over its own cap. The plan wall is FREE's, so the honest answer
		// is the existing upgrade nudge — rendered by the panel, not here. `signInRequired` renders
		// nothing (owner decision 2026-10-09, kitecorp/transferify-ui#3435): the signed-out sender gives the
		// e-mail every send asks for, the usual Send signs them in, and the price is met after that.
		return null;
	}
	if (resolution.kind === "aboveLargestPlan") {
		// Above what the largest plan holds (kitecorp/transferify-api-service#3005): the limit and the way forward in place
		// of any price or upgrade, and no control, because the way forward is a smaller selection.
		return strip(<span>{copy.aboveLargestPlan}</span>);
	}
	if (resolution.kind === "fileListRefused") {
		// A selection the upload init would refuse whatever was paid (ui#2930): the rule and the way forward in place
		// of any price, and no control, because the way forward is a smaller or zipped selection.
		return strip(<span>{copy.fileListRefused[resolution.rule]}</span>);
	}
	if (resolution.kind === "paid") {
		return strip(<span>{copy.paid}</span>);
	}
	if (confirming) {
		return strip(<span>{copy.confirming}</span>);
	}
	if (resolution.kind === "quoting" || resolution.kind === "checkingPurchase") {
		// No CTA before the price exists: a button pressed here would quote a figure that then changes.
		// `checkingPurchase` reads the same, and for a sender who has already paid it lasts one read:
		// "working out the price" is true for them too, and a sentence naming a purchase would puzzle
		// every sender who never made one (kitecorp/transferify-ui#3010).
		return strip(<span>{copy.quoting}</span>);
	}

	if (resolution.kind === "planRequired" && overLimitAction(resolution) === null) {
		// The Instant wall on a storefront where the api offers no plan row (kitecorp/transferify-api-service#3005): the
		// wall in a sentence and no control, since there is nothing to press. The send stays held shut, and the way
		// forward is a smaller selection.
		return strip(<span>{planRequiredSentence}</span>);
	}

	/*
	 * ONE control, shared by every rung that offers the sender an action — the same element rather than
	 * a button per branch. That is the owner's single-CTA constraint expressed structurally, and it is
	 * what `scripts/over-limit-single-cta-sweep.mjs` counts: three branches each rendering their own
	 * control would read as three CTAs to the sweep and be three to maintain.
	 *
	 * WHAT the control does is `overLimitAction`'s decision, not this view's; the view supplies the
	 * localized sentence and label for it, which is the only thing here a rules module could not.
	 */
	const action = overLimitAction(resolution);
	const control = ((): {sentence: React.ReactNode; label: string; run: () => void} | null => {
		switch (action) {
			case "retry":
				return {sentence: copy.quoteFailed, label: copy.retry, run: onRetry};
			case "plan":
				// The plan press of the size wall on either path (kitecorp/transferify-ui#3800): its dialog lists every plan that
				// holds the drop, the smallest chosen. The sentence names the wall; the LABEL names no price and reads as
				// navigation (kitecorp/transferify-ui#3274), because the press opens the dialog.
				return resolution.kind === "planRequired"
					? {sentence: planRequiredSentence, label: copy.openPayment, run: onChoosePlan}
					: null;
			case null:
				return null;
			default:
				// A new ACTION added to the union must be given its sentence and label here. Without this
				// the switch would fall through to a spinner with no control — the very dead end ui#1715
				// removed — and it would compile.
				return assertNever(action);
		}
	})();
	if (control === null) {
		return strip(<span>{copy.quoting}</span>);
	}
	return (
		strip(<>
			<span>{control.sentence}</span>
			{/* CALLED, never forwarded: handing `run` to `onClick` directly would pass the React synthetic
			    event as its first argument, which is how an opener that takes an optional return-focus
			    element once failed to open at all (ui#933, `src/components/sheetOpener.ts`). The wrapper
			    keeps that unreachable for every action this control is ever given. */}
			<PayAction type="button" onClick={() => control.run()}>
				{control.label}
			</PayAction>
		</>)
	);
}
